import type { Router, RouteLocationRaw, RouteLocationNormalized } from 'vue-router';


type AuthRouter = Pick<Router, 'currentRoute' | 'replace'>;
type Recheck = { resolve: () => void; reject: (error: unknown) => void };
type TrackedNavigation = { pending: RouteLocationNormalized | null; queued: Recheck[] };
const navigations = new WeakMap<AuthRouter, TrackedNavigation>();

/** Token renewal alone does not change route access; the server remains the permission authority. */
export function authNavigationAccessIdentity(auth: { token: string | null; user: { id: string; roles: readonly string[] } | null }) {
  return JSON.stringify([Boolean(auth.token && auth.user), auth.user?.id, [...(auth.user?.roles ?? [])].sort()]);
}

async function revalidateCurrentRoute(router: AuthRouter) {
  const current = router.currentRoute.value;
  if (current.meta?.terminal === true || current.meta?.public === true) return;
  await router.replace({ path: current.path, query: current.query, hash: current.hash, force: true });
}

/** Install before the authorization guard so refreshes inside that guard cannot cancel its user's destination. */
export function trackAuthNavigation(router: Router) {
  if (navigations.has(router)) return;
  const state: TrackedNavigation = { pending: null, queued: [] };
  navigations.set(router, state);
  router.beforeEach(to => { state.pending = to; });
  const settled = (to: RouteLocationNormalized) => {
    if (state.pending !== to) return; // A superseded navigation cannot settle the newer one.
    state.pending = null;
    const queued = state.queued.splice(0);
    if (queued.length === 0) return;
    void revalidateCurrentRoute(router).then(
      () => queued.forEach(waiter => waiter.resolve()),
      error => queued.forEach(waiter => waiter.reject(error))
    );
  };
  router.afterEach(to => settled(to));
  router.onError((_error, to) => settled(to));
}

/** Office-session changes reuse the router's protected-route login redirect. */
export async function handleAuthNavigation(
  router: AuthRouter,
  isAuthed: boolean,
  resolveHome: () => Promise<RouteLocationRaw>,
  revalidateProtected = false
) {
  const currentRoute = router.currentRoute.value;
  if (currentRoute.meta?.terminal === true) return;
  if (isAuthed && (currentRoute.meta?.public || currentRoute.name === 'login' || currentRoute.name === 'login-qr')) {
    const redirect = typeof currentRoute.query.redirect === 'string' ? currentRoute.query.redirect : '';
    const localRedirect = redirect.startsWith('/') && !redirect.startsWith('//');
    await router.replace(localRedirect ? redirect : await resolveHome());
    return;
  }
  if (isAuthed && revalidateProtected && currentRoute.meta?.public !== true) {
    const active = navigations.get(router);
    if (active?.pending) {
      await new Promise<void>((resolve, reject) => { active.queued.push({ resolve, reject }); });
    } else await revalidateCurrentRoute(router);
    return;
  }
  if (!isAuthed && currentRoute.meta?.public !== true) {
    await router.replace({ name: 'login', query: { redirect: currentRoute.fullPath } });
  }
}
